// =============================================================================
// AnnouncementBanner.js, top-of-app marketing/ops banner for users.
//
// Behaviour:
//   • Fetches the single active announcement on mount + when user changes.
//   • User dismissal persists in localStorage keyed by `{annId}:{userId}`.
//   • On a fresh sign-in (new auth user id detected), all dismissals are
//     cleared so the banner re-appears as the user requested.
//   • Fires impression/dismiss/click pings (server-side deduped per user).
//   • Banner palette tuned for premium quiet-luxury feel (Phase 21).
// =============================================================================
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Info, AlertTriangle, AlertOctagon, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY_PREFIX = 'announcement_dismissed_';
const LAST_AUTH_USER_KEY = 'announcement_last_auth_uid';

// Severity palette, explicit hex so it renders identically in light/dark themes.
const SEV = {
  info: {
    bg: 'hsl(var(--primary) / 0.08)',
    border: 'hsl(var(--primary) / 0.35)',
    text: '#214438',
    icon: Info,
  },
  warning: {
    bg: '#FFF7E8',
    border: '#D6A85C',
    text: '#6E5323',
    icon: AlertTriangle,
  },
  critical: {
    bg: '#FCEEEE',
    border: '#C96B6B',
    text: '#6A2F2F',
    icon: AlertOctagon,
  },
};

export default function AnnouncementBanner() {
  const { session, user } = useAuth();
  const token = session?.access_token;
  const uid = user?.id || null;
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const viewPingedRef = useRef(new Set()); // announcement ids we've already pinged this mount

  // When a new auth user is detected, clear all prior dismissal records.
  useEffect(() => {
    if (!uid) return;
    try {
      const last = localStorage.getItem(LAST_AUTH_USER_KEY);
      if (last !== uid) {
        Object.keys(localStorage)
          .filter((k) => k.startsWith(STORAGE_KEY_PREFIX))
          .forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(LAST_AUTH_USER_KEY, uid);
      }
    } catch { /* storage may be unavailable */ }
  }, [uid]);

  const pingEvent = useCallback((annId, eventType) => {
    if (!annId || !token) return;
    // Fire-and-forget; never block the UI.
    axios.post(
      `${BACKEND_URL}/api/announcements/${annId}/event`,
      { event_type: eventType },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 4000 },
    ).catch(() => { /* swallow */ });
  }, [token]);

  const load = useCallback(async () => {
    if (!token) { setAnn(null); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/announcements/active`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 6000,
      });
      const a = r.data?.announcement || null;
      setAnn(a);
      if (a && uid) {
        const key = `${STORAGE_KEY_PREFIX}${a.id}:${uid}`;
        const isDismissed = localStorage.getItem(key) === '1';
        setDismissed(isDismissed);
        // Fire impression ping only if not dismissed and not already pinged this session.
        if (!isDismissed && !viewPingedRef.current.has(a.id)) {
          viewPingedRef.current.add(a.id);
          pingEvent(a.id, 'view');
        }
      } else {
        setDismissed(false);
      }
    } catch { /* silent, banner is non-critical */ }
  }, [token, uid, pingEvent]);

  useEffect(() => { load(); }, [load]);

  const onDismiss = () => {
    if (!ann || !uid) return;
    try { localStorage.setItem(`${STORAGE_KEY_PREFIX}${ann.id}:${uid}`, '1'); } catch { /* ignore */ }
    pingEvent(ann.id, 'dismiss');
    setDismissed(true);
  };

  const onCta = () => {
    if (!ann) return;
    pingEvent(ann.id, 'click');
    // Link itself handles navigation via target="_blank".
  };

  if (!ann || dismissed) return null;
  const sev = SEV[ann.severity] || SEV.info;
  const Icon = sev.icon;

  return (
    <div
      className="w-full backdrop-blur-sm"
      style={{
        backgroundColor: sev.bg,
        borderBottom: `1px solid ${sev.border}`,
        color: sev.text,
      }}
      role="status"
      aria-live="polite"
      data-testid="global-announcement-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-start gap-3">
        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-xs sm:text-sm">
          <span>{ann.body}</span>
          {ann.link_url && (
            <a
              href={ann.link_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCta}
              className="ml-2 inline-flex items-center gap-1 underline-offset-2 hover:underline font-medium"
              style={{ color: sev.text }}
              data-testid="announcement-cta-link"
            >
              {ann.link_label || 'Learn more'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] opacity-75 hover:opacity-100 transition-opacity"
          style={{ color: sev.text }}
          aria-label="Dismiss announcement"
          data-testid="announcement-dismiss"
        >
          <span className="hidden sm:inline">Dismiss</span>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
